/**
 * Words for the office events the job timeline shows (GET /v1/jobs/:id/audit).
 */

import { JOB_SITE_CHANGE_EVENT_LABELS } from '@lodex-pro/shared/audit/job-site-change-events';
import {
  JOB_SITE_CHANGE_REASON_LABELS,
  type JobSiteChangeReasonCode,
} from '@lodex-pro/shared/jobs/site-change';
import type { AuditEvent } from './use-completed-jobs';

/**
 * OPEN-157 — the office moved a started job off its driver (take back), or corrected a finished
 * ticket's driver. The label for the Basic view's row; null for any other event.
 */
export function driverChangeLabel(e: AuditEvent): string | null {
  switch (e.eventType) {
    case 'JOB_TAKEN_BACK':
      return 'Taken back';
    case 'JOB_DRIVER_CORRECTED':
      return 'Driver corrected';
    default:
      return null;
  }
}

/** "A → B · “reason”" (or "A → Unassigned" for a take-back to nobody). */
export function driverChangeDetail(e: AuditEvent): string {
  const from = e.driverFrom ?? 'Unknown driver';
  const to = e.driverTo ?? (e.eventType === 'JOB_TAKEN_BACK' ? 'Unassigned' : 'Unknown driver');
  const reason = e.editReason?.trim() ?? '';
  return reason === '' ? `${from} → ${to}` : `${from} → ${to} · “${reason}”`;
}

/** The Detailed view's line: "Taken back: A → B · “reason”". Null for any other event. */
export function driverChangeEventText(e: AuditEvent): string | null {
  const label = driverChangeLabel(e);
  return label === null ? null : `${label}: ${driverChangeDetail(e)}`;
}

/** Billing changed the approved ticket's billed lines (#50, DEC-356). */
export const BILLING_TICKET_EDITED_EVENT_TYPE = 'BILLING_TICKET_EDITED';

/** "Billing edited the ticket · “reason”" — the reason exactly as billing typed it. */
export function billingTicketEditedText(e: AuditEvent): string {
  const reason = e.editReason?.trim() ?? '';
  return reason === '' ? 'Billing edited the ticket' : `Billing edited the ticket · “${reason}”`;
}

/**
 * #60 (DEC-365) — the pickup-site events: "Site changed: A → B · reason — “words”", a driver's
 * change sent to the office, one the office applied, and a tank re-picked after a change. The
 * names are the API's snapshot (siteFrom / siteTo); the actor and "offline" ride the row's
 * own badges. Null for any other event.
 */
export function siteChangeEventText(e: AuditEvent): string | null {
  const why = siteChangeReasonText(e);
  switch (e.eventType) {
    case 'JOB_SITE_CHANGED': {
      const route =
        e.siteFrom != null || e.siteTo != null
          ? `: ${e.siteFrom ?? 'no site'} → ${e.siteTo ?? 'no site'}`
          : '';
      return `${JOB_SITE_CHANGE_EVENT_LABELS.JOB_SITE_CHANGED}${route}${why}`;
    }
    case 'JOB_SITE_CHANGE_PARKED':
      return `${JOB_SITE_CHANGE_EVENT_LABELS.JOB_SITE_CHANGE_PARKED}${why}`;
    case 'JOB_SITE_CHANGE_APPLIED':
      return JOB_SITE_CHANGE_EVENT_LABELS.JOB_SITE_CHANGE_APPLIED;
    case 'HAULER_TANK_RELINKED':
      return JOB_SITE_CHANGE_EVENT_LABELS.HAULER_TANK_RELINKED;
    default:
      return null;
  }
}

function siteChangeReasonText(e: AuditEvent): string {
  const code = e.siteChangeReasonCode ?? null;
  const label =
    code !== null && code in JOB_SITE_CHANGE_REASON_LABELS
      ? JOB_SITE_CHANGE_REASON_LABELS[code as JobSiteChangeReasonCode]
      : null;
  const words = e.editReason?.trim() ?? '';
  const parts = [
    label !== null && code !== 'OTHER' ? label : null,
    words === '' ? null : `“${words}”`,
  ].filter((p): p is string => p !== null);
  return parts.length === 0 ? '' : ` · ${parts.join(' — ')}`;
}
